import { NET, STREAM, T, cellBounds, cellCentre, cellSize, isReach, routePolyline, streamPoint, systemLayout, type AsteroidBody, type SphereConfig, type Vec2, type WorldDef } from "@dyson/shared";
import { MAP_COLOR } from "../render/TileAtlas";

/** Resolution of the pre-drawn world image (the whole sphere). */
const BACKDROP_PX = 2048;
/** World units across the small map, which follows you. */
const SMALL_SPAN = 1800;
/** Asteroid edits (and newly explored cells) redraw the backdrop at most this often (ms). */
const REDRAW_MS = 1000;
/** Fog of war is kept on a grid of cells this many blocks across. */
const FOG_CELL = 100;
/** How long a boarding flash pulses (ms). */
const FLASH_MS = 2000;

export interface MapShip {
  /** Centre of the ship's grid, in world units. */
  center: Vec2;
  angle: number;
  w: number;
  h: number;
  /** The ship you are aboard, drawn highlighted. */
  aboard: boolean;
  /** No helm (broken-off chunks, loose blocks): drawn as small grey specks. */
  drifting: boolean;
  /** Pirates under way: drawn red. */
  pirate: boolean;
}

export interface MapView {
  me?: Vec2;
  ships: MapShip[];
  /** Everyone else's world centre. */
  others: Vec2[];
  /** Creatures (whales and the like), drawn as small teal dots. */
  creatures?: Vec2[];
}


/**
 * Map in the bottom-right corner. Small, it shows the area around you;
 * clicked, it opens over the screen showing the whole sphere, and a click
 * there asks to teleport to that spot.
 *
 * Rock you haven't been near is fogged: cells of the sphere are marked
 * explored as they come within view, and remembered per world seed. Suns
 * always show, being lights you can see from anywhere.
 */
export class Minimap {
  /** A world point picked on the big map. */
  onTeleport: (at: Vec2) => void = () => {};

  private readonly root = document.getElementById("map")!;
  private readonly canvas = document.getElementById("map-canvas") as HTMLCanvasElement;
  private readonly ctx = this.canvas.getContext("2d")!;
  private readonly backdrop = document.createElement("canvas");
  /** Every asteroid tile, fog or not: only redrawn when rock changes, then laid into the backdrop under the fog. */
  private readonly terrain = document.createElement("canvas");
  /** Explored fog cells, one byte each, `fogN` by `fogN`, row 0 at the top (+y). */
  private explored = new Uint8Array(0);
  private fogN = 0;
  /** Cells were explored since the backdrop was last drawn (and saved). */
  private fogDirty = false;
  private flashes: { at: Vec2; until: number }[] = [];
  private world?: WorldDef;
  /** How the sphere is split into areas, for drawing their borders. */
  private sphere?: SphereConfig;
  private asteroids: AsteroidBody[] = [];
  private dirty = false;
  /** How bright the stars are while the sphere shuts down (see Outage.output), in twentieths, and whether that's changed since they were drawn. */
  private sunOutput = 20;
  private sunsDirty = false;

  /** How bright every star is now (0..1): dark, they're drawn as embers. */
  setSunOutput(output: number): void {
    const next = Math.round(output * 20);
    if (next === this.sunOutput) return;
    this.sunOutput = next;
    this.sunsDirty = true;
  }
  private drawnAt = 0;
  private big = false;
  /** Mouse over the big map, in CSS pixels from its top-left. */
  private hover: { x: number; y: number } | null = null;

  constructor() {
    this.backdrop.width = this.backdrop.height = BACKDROP_PX;
    this.terrain.width = this.terrain.height = BACKDROP_PX;
    // Keep what was explored since the last save when the tab closes.
    window.addEventListener("pagehide", () => this.saveFog());
    this.canvas.addEventListener("click", (e) => {
      if (!this.big) return this.setBig(true);
      const at = this.toWorld(e.offsetX, e.offsetY);
      if (this.world && Math.hypot(at.x, at.y) < this.world.worldRadius) {
        this.onTeleport(at);
        this.setBig(false);
      }
    });
    this.canvas.addEventListener("mousemove", (e) => (this.hover = { x: e.offsetX, y: e.offsetY }));
    this.canvas.addEventListener("mouseleave", () => (this.hover = null));
    document.getElementById("map-close")!.addEventListener("click", () => this.setBig(false));
    window.addEventListener("keydown", (e) => {
      if (e.code === "Escape" && this.big) this.setBig(false);
    });
  }

  get isBig(): boolean {
    return this.big;
  }

  setBig(big: boolean): void {
    this.big = big;
    this.hover = null;
    this.root.classList.toggle("big", big);
  }

  setWorld(world: WorldDef, asteroids: AsteroidBody[], sphere?: SphereConfig): void {
    this.world = world;
    this.sphere = sphere;
    this.asteroids = asteroids;
    this.loadFog();
    this.drawTerrain();
    this.drawBackdrop();
  }

  /** The asteroids loaded changed (the player moved on): drawn again shortly. */
  setAsteroids(asteroids: AsteroidBody[]): void {
    this.asteroids = asteroids;
    this.dirty = true;
  }

  /** Pulse a ring at a world point for a couple of seconds: someone just boarded your ship. */
  flash(at: Vec2): void {
    this.flashes.push({ at: { x: at.x, y: at.y }, until: performance.now() + FLASH_MS });
  }

  /** An asteroid tile changed; the backdrop catches up within a second. */
  markDirty(): void {
    this.dirty = true;
  }

  draw(view: MapView): void {
    const world = this.world;
    if (!world) return;
    if (view.me) this.explore(view.me);
    if ((this.dirty || this.fogDirty || this.sunsDirty) && performance.now() - this.drawnAt > REDRAW_MS) {
      this.sunsDirty = false;
      if (this.dirty) this.drawTerrain();
      this.drawBackdrop();
    }

    const dpr = window.devicePixelRatio || 1;
    const cssW = this.canvas.clientWidth;
    const cssH = this.canvas.clientHeight;
    if (this.canvas.width !== Math.round(cssW * dpr) || this.canvas.height !== Math.round(cssH * dpr)) {
      this.canvas.width = Math.round(cssW * dpr);
      this.canvas.height = Math.round(cssH * dpr);
    }
    const ctx = this.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#040308";
    ctx.fillRect(0, 0, cssW, cssH);

    // World -> CSS pixels: centred on you when small, on the whole sphere when big.
    const { cx, cy, scale } = this.frame(view.me);
    const px = (p: Vec2) => ({ x: cssW / 2 + (p.x - cx) * scale, y: cssH / 2 - (p.y - cy) * scale });

    const R = world.worldRadius;
    const tl = px({ x: -R, y: R });
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(this.backdrop, tl.x, tl.y, 2 * R * scale, 2 * R * scale);
    this.drawAreas(ctx, px, scale, R);
    this.drawNames(ctx, px);
    // Drifting rocks near us, where they are now (they're not in the terrain: it'd be stale). Nuggets stand out: rust for iron, black for coal.
    for (const a of this.asteroids) {
      if (!a.def.drift) continue;
      ctx.fillStyle = a.def.kind === "iron" ? "#c8763a" : a.def.kind === "coal" ? "#16130f" : "#9a8f80";
      const c = px({ x: a.def.ox + a.def.w / 2, y: a.def.oy + a.def.h / 2 });
      const r = Math.max(1, a.def.radius * scale);
      ctx.fillRect(c.x - r, c.y - r, r * 2, r * 2);
    }

    ctx.fillStyle = "#8a8378";
    for (const s of view.ships) {
      if (!s.drifting) continue;
      const c = px(s.center);
      ctx.fillRect(c.x - 1, c.y - 1, 2, 2);
    }
    for (const s of view.ships) {
      if (s.drifting) continue;
      const c = px(s.center);
      ctx.save();
      ctx.translate(c.x, c.y);
      ctx.rotate(-s.angle);
      ctx.fillStyle = s.aboard ? "#ffc070" : s.pirate ? "#ff4a3a" : "#d8cdb4";
      const w = Math.max(5, s.w * scale);
      const h = Math.max(3, s.h * scale);
      ctx.fillRect(-w / 2, -h / 2, w, h);
      ctx.restore();
    }
    ctx.fillStyle = "#5fd8c8";
    for (const o of view.creatures ?? []) {
      const c = px(o);
      ctx.fillRect(c.x - 1.5, c.y - 1.5, 3, 3);
    }
    ctx.fillStyle = "#8fd0ff";
    for (const o of view.others) {
      const c = px(o);
      ctx.beginPath();
      ctx.arc(c.x, c.y, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
    if (view.me) {
      const c = px(view.me);
      ctx.fillStyle = "#ffffff";
      ctx.strokeStyle = "rgba(255,255,255,0.7)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(c.x, c.y, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(c.x, c.y, 7, 0, Math.PI * 2);
      ctx.stroke();
    }

    const now = performance.now();
    this.flashes = this.flashes.filter((f) => f.until > now);
    for (const f of this.flashes) {
      const c = px(f.at);
      // Three expanding rings over the two seconds.
      const pulse = ((1 - (f.until - now) / FLASH_MS) * 3) % 1;
      ctx.strokeStyle = `rgba(255,90,58,${((1 - pulse) * 0.9).toFixed(2)})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(c.x, c.y, 5 + pulse * 18, 0, Math.PI * 2);
      ctx.stroke();
    }

    if (this.big && this.hover) {
      const { x, y } = this.hover;
      const at = this.toWorld(x, y);
      const inside = Math.hypot(at.x, at.y) < R;
      ctx.strokeStyle = inside ? "rgba(191,232,160,0.9)" : "rgba(217,88,59,0.9)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x - 10, y);
      ctx.lineTo(x + 10, y);
      ctx.moveTo(x, y - 10);
      ctx.lineTo(x, y + 10);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  /** On the big map: every star's name under it, and the stream's named corners (sweeps are the stars'; straights and slight bends go unlabelled). */
  private drawNames(ctx: CanvasRenderingContext2D, px: (p: Vec2) => { x: number; y: number }): void {
    const world = this.world;
    if (!world || !this.big) return;
    ctx.save();
    ctx.font = "11px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = "rgba(255,230,180,0.85)";
    for (const s of world.suns) {
      if (!s.name) continue;
      const c = px(s.position);
      ctx.fillText(s.name, c.x, c.y + 8);
    }
    const route = world.stream;
    if (route) {
      ctx.fillStyle = "rgba(190,220,255,0.9)";
      ctx.textBaseline = "middle";
      for (const pl of route.places) {
        if (pl.star || pl.kind === "straight" || pl.kind === "bend") continue;
        const { p, dir } = streamPoint(route, pl.kind === "narrows" || pl.kind === "gorge" ? pl.s : pl.s + pl.len / 2);
        const c = px(p);
        ctx.fillRect(c.x - 1.5, c.y - 1.5, 3, 3);
        ctx.textAlign = dir.y > 0 ? "right" : "left";
        ctx.fillText(pl.name, c.x + (dir.y > 0 ? -5 : 5), c.y);
      }
    }
    ctx.restore();
  }

  /**
   * The borders between areas (each simulated on its own server), as faint
   * dashed lines across the sphere; on the big map each area is labelled with
   * its coordinates. Nothing for a sphere that's one area.
   */
  private drawAreas(ctx: CanvasRenderingContext2D, px: (p: Vec2) => { x: number; y: number }, scale: number, R: number): void {
    const size = this.sphere?.areaSize;
    if (!size || size > R * 2) return;
    // Area (ax, ay) covers [ax * size - size/2, ...): borders are at odd multiples of size/2.
    const first = Math.ceil((-R + size / 2) / size);
    const last = Math.floor((R + size / 2) / size);
    ctx.save();
    ctx.strokeStyle = "rgba(168,115,63,0.55)";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    for (let k = first; k <= last; k++) {
      const at = k * size - size / 2;
      // Only the part inside the shell.
      const half = Math.sqrt(Math.max(0, R * R - at * at));
      if (!half) continue;
      let a = px({ x: at, y: -half });
      let b = px({ x: at, y: half });
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      a = px({ x: -half, y: at });
      b = px({ x: half, y: at });
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
    }
    ctx.stroke();
    ctx.restore();
    // Labels only where there's room for them.
    if (size * scale < 40) return;
    ctx.save();
    ctx.fillStyle = "rgba(168,115,63,0.8)";
    ctx.font = "10px sans-serif";
    ctx.textBaseline = "top";
    for (let ay = first - 1; ay <= last; ay++) {
      for (let ax = first - 1; ax <= last; ax++) {
        const corner = { x: ax * size - size / 2, y: ay * size + size / 2 };
        if (Math.hypot(ax * size, ay * size) > R) continue;
        const p = px(corner);
        ctx.fillText(`${ax},${ay}`, p.x + 3, p.y + 3);
      }
    }
    ctx.restore();
  }

  private frame(me: Vec2 | undefined): { cx: number; cy: number; scale: number } {
    const R = this.world!.worldRadius;
    const size = Math.min(this.canvas.clientWidth, this.canvas.clientHeight);
    if (this.big || !me) return { cx: 0, cy: 0, scale: size / (2 * R * 1.03) };
    return { cx: me.x, cy: me.y, scale: size / SMALL_SPAN };
  }

  private toWorld(x: number, y: number): Vec2 {
    const { cx, cy, scale } = this.frame(undefined);
    return { x: cx + (x - this.canvas.clientWidth / 2) / scale, y: cy - (y - this.canvas.clientHeight / 2) / scale };
  }

  /** An eddy, once found: a ring of rock-coloured arcs, each with a head the way it turns. */
  private drawEddy(g: CanvasRenderingContext2D, at: Vec2, radius: number, spin: number, R: number, k: number): void {
    if (!this.isExplored(at.x, at.y)) return;
    const x = (at.x + R) * k;
    const y = (R - at.y) * k;
    const r = Math.max(4, radius * k * 0.75);
    // Map y runs down, so anticlockwise in the world is clockwise here.
    const dir = spin > 0 ? -1 : 1;
    g.save();
    g.strokeStyle = "rgba(170,160,140,0.6)";
    g.lineWidth = 2;
    for (let i = 0; i < 3; i++) {
      const a0 = (i / 3) * Math.PI * 2;
      const a1 = a0 + dir * 1.4;
      g.beginPath();
      g.arc(x, y, r, a0, a1, dir < 0);
      const hx = x + Math.cos(a1) * r;
      const hy = y + Math.sin(a1) * r;
      // The head: along the arc's tangent, two barbs swept back.
      const tx = -Math.sin(a1) * dir;
      const ty = Math.cos(a1) * dir;
      const len = Math.min(6, r * 0.5);
      g.moveTo(hx - tx * len + ty * len * 0.6, hy - ty * len - tx * len * 0.6);
      g.lineTo(hx, hy);
      g.lineTo(hx - tx * len - ty * len * 0.6, hy - ty * len + tx * len * 0.6);
      g.stroke();
    }
    g.restore();
  }

  /** Every asteroid tile, pre-drawn once (and after edits), fog or no fog. Not rocks turning in an eddy: the eddy's drawn instead (see drawBackdrop). */
  private drawTerrain(): void {
    const R = this.world!.worldRadius;
    const S = BACKDROP_PX;
    const k = S / (2 * R);
    const g = this.terrain.getContext("2d")!;
    g.clearRect(0, 0, S, S);
    const image = g.getImageData(0, 0, S, S);
    const pixels = new Uint32Array(image.data.buffer);
    for (const a of this.asteroids) {
      if (a.def.drift) continue;
      const { w, h, tiles } = a.grid;
      for (let y = 0; y < h; y++) {
        const py = Math.floor((R - (a.def.oy + y + 0.5)) * k);
        if (py < 0 || py >= S) continue;
        for (let x = 0; x < w; x++) {
          const t = tiles[y * w + x]!;
          if (t === T.EMPTY) continue;
          const pxl = Math.floor((a.def.ox + x + 0.5 + R) * k);
          if (pxl < 0 || pxl >= S) continue;
          pixels[py * S + pxl] = MAP_COLOR[t]!;
        }
      }
    }
    g.putImageData(image, 0, 0);
    this.dirty = false;
  }

  /**
   * The sphere as the map shows it: the terrain where you've explored,
   * hatched dark fog where you haven't, then currents and pirate bases
   * you've found, and the suns (always shown) on top.
   */
  private drawBackdrop(): void {
    const world = this.world!;
    const R = world.worldRadius;
    const S = BACKDROP_PX;
    const k = S / (2 * R);
    const g = this.backdrop.getContext("2d")!;
    g.clearRect(0, 0, S, S);
    g.save();
    g.beginPath();
    g.arc(S / 2, S / 2, S / 2 - 2, 0, Math.PI * 2);
    g.fillStyle = "#0d0b16";
    g.fill();
    g.clip();
    g.drawImage(this.terrain, 0, 0);

    // Fog: one rect per unexplored cell, filled dark and then hatched.
    const n = this.fogN;
    const cellPx = FOG_CELL * k;
    g.beginPath();
    for (let cy = 0; cy < n; cy++) {
      for (let cx = 0; cx < n; cx++) {
        if (!this.explored[cy * n + cx]) g.rect(Math.floor(cx * cellPx), Math.floor(cy * cellPx), Math.ceil(cellPx) + 1, Math.ceil(cellPx) + 1);
      }
    }
    g.fillStyle = "#07060c";
    g.fill();
    g.fillStyle = g.createPattern(hatch(), "repeat")!;
    g.fill();
    g.restore();
    g.strokeStyle = "#a8733f";
    g.lineWidth = 3;
    g.beginPath();
    g.arc(S / 2, S / 2, S / 2 - 2, 0, Math.PI * 2);
    g.stroke();

    // Eddies: a swirl the way each turns.
    g.strokeStyle = "rgba(150,200,255,0.35)";
    g.lineWidth = 1.5;
    for (const c of world.currents ?? []) if (c.spin) this.drawEddy(g, c.position, c.radius, c.spin, R, k);

    // Pirate bases, once found, ringed in red.
    g.strokeStyle = "rgba(255,74,58,0.85)";
    g.lineWidth = 2;
    for (const a of this.asteroids) {
      if (!a.def.pirateBase) continue;
      const cx = a.def.ox + a.def.w / 2;
      const cy = a.def.oy + a.def.h / 2;
      if (!this.isExplored(cx, cy)) continue;
      g.beginPath();
      g.arc((cx + R) * k, (R - cy) * k, a.def.radius * k + 5, 0, Math.PI * 2);
      g.stroke();
    }

    // The dark reaches (cells with no star), as a void you can see the edge of; and each star's rings, faintly, seen from anywhere (as the stars are).
    if (this.sphere) {
      const size = cellSize(this.sphere);
      const o = -this.sphere.areaSize / 2;
      const n = Math.ceil(R / size) + 1;
      g.strokeStyle = "rgba(60,50,90,0.5)";
      g.lineWidth = 1;
      g.setLineDash([3, 5]);
      for (let cy = -n; cy <= n; cy++) {
        for (let cx = -n; cx <= n; cx++) {
          const c = cellCentre(this.sphere, cx, cy);
          if (Math.hypot(c.x, c.y) > R * 0.78 || !isReach(this.sphere, cx, cy)) continue;
          const b = cellBounds(this.sphere, cx, cy);
          g.strokeRect((b.x0 + R) * k + 2, (R - b.y1) * k + 2, (b.x1 - b.x0) * k - 4, (b.y1 - b.y0) * k - 4);
        }
      }
      g.setLineDash([]);
      g.strokeStyle = "rgba(154,143,128,0.22)";
      for (const s of world.suns) {
        const layout = systemLayout(this.sphere, Math.floor((s.position.x - o) / size), Math.floor((s.position.y - o) / size));
        if (!layout) continue;
        for (const ring of layout.rings) {
          g.lineWidth = Math.max(1, (ring.r1 - ring.r0) * k);
          g.beginPath();
          g.arc((s.position.x + R) * k, (R - s.position.y) * k, ((ring.r0 + ring.r1) / 2) * k, 0, Math.PI * 2);
          g.stroke();
        }
      }
    }

    // The stream's track (see stream_plan.md): its band, with arrows the way it flows, and its named corners.
    const route = world.stream;
    if (route) {
      g.strokeStyle = "rgba(120,170,255,0.55)";
      g.lineWidth = Math.max(1.5, STREAM.band * k);
      g.lineJoin = "round";
      g.beginPath();
      routePolyline(route, 40).forEach(({ p }, i) => (i ? g.lineTo((p.x + R) * k, (R - p.y) * k) : g.moveTo((p.x + R) * k, (R - p.y) * k)));
      g.closePath();
      g.stroke();
      g.strokeStyle = "rgba(190,220,255,0.9)";
      g.lineWidth = 1.5;
      g.beginPath();
      for (let s = 0; s < route.length; s += 1500) {
        const { p, dir } = streamPoint(route, s);
        const x = (p.x + R) * k;
        const y = (R - p.y) * k;
        // Map y runs down, so the flow's y is flipped.
        const ux = dir.x;
        const uy = -dir.y;
        const len = 7;
        g.moveTo(x - (ux * 0.8 - uy * 0.6) * len, y - (uy * 0.8 + ux * 0.6) * len);
        g.lineTo(x, y);
        g.lineTo(x - (ux * 0.8 + uy * 0.6) * len, y - (uy * 0.8 - ux * 0.6) * len);
      }
      g.stroke();
    }

    for (const s of world.suns) {
      const x = (s.position.x + R) * k;
      const y = (R - s.position.y) * k;
      const r = s.radius * k;
      // While the sphere's shut down (see NIGHT): the glow goes, and out, each star's a dull red ember.
      const out = this.sunOutput / 20;
      const glow = g.createRadialGradient(x, y, 0, x, y, r * 3);
      glow.addColorStop(0, `rgba(255,230,160,${0.55 * out})`);
      glow.addColorStop(1, "rgba(255,200,120,0)");
      g.fillStyle = glow;
      g.beginPath();
      g.arc(x, y, r * 3, 0, Math.PI * 2);
      g.fill();
      const lit = [(s.color >> 16) & 255, (s.color >> 8) & 255, s.color & 255];
      const ember = [110, 34, 22];
      g.fillStyle = `rgb(${lit.map((c, i) => Math.round(ember[i]! + (c - ember[i]!) * out)).join(",")})`;
      g.beginPath();
      g.arc(x, y, Math.max(2, r), 0, Math.PI * 2);
      g.fill();
    }
    this.drawnAt = performance.now();
    if (this.fogDirty) this.saveFog();
    this.fogDirty = false;
  }

  /** Mark the fog cells within view of `me` explored. */
  private explore(me: Vec2): void {
    const n = this.fogN;
    if (!n) return;
    const R = this.world!.worldRadius;
    // A cell counts as seen if any of it is in view: its centre within the radius plus half its diagonal.
    const reach = NET.viewRadius + FOG_CELL * 0.71;
    const x0 = Math.max(0, Math.floor((me.x - reach + R) / FOG_CELL));
    const x1 = Math.min(n - 1, Math.floor((me.x + reach + R) / FOG_CELL));
    const y0 = Math.max(0, Math.floor((R - (me.y + reach)) / FOG_CELL));
    const y1 = Math.min(n - 1, Math.floor((R - (me.y - reach)) / FOG_CELL));
    for (let cy = y0; cy <= y1; cy++) {
      const wy = R - (cy + 0.5) * FOG_CELL;
      for (let cx = x0; cx <= x1; cx++) {
        const i = cy * n + cx;
        if (this.explored[i]) continue;
        const wx = (cx + 0.5) * FOG_CELL - R;
        if ((wx - me.x) ** 2 + (wy - me.y) ** 2 > reach * reach) continue;
        this.explored[i] = 1;
        this.fogDirty = true;
      }
    }
  }

  private isExplored(x: number, y: number): boolean {
    const R = this.world!.worldRadius;
    const cx = Math.floor((x + R) / FOG_CELL);
    const cy = Math.floor((R - y) / FOG_CELL);
    return cx >= 0 && cy >= 0 && cx < this.fogN && cy < this.fogN && !!this.explored[cy * this.fogN + cx];
  }

  private fogKey(): string {
    return `dyson.explored.${this.world!.seed}`;
  }

  /** Restore this world's explored cells, saved as "<n>:<base64 bitset>". Anything that doesn't fit starts fresh. */
  private loadFog(): void {
    const n = Math.ceil((2 * this.world!.worldRadius) / FOG_CELL);
    this.fogN = n;
    this.explored = new Uint8Array(n * n);
    this.fogDirty = false;
    try {
      const m = /^(\d+):(.*)$/.exec(localStorage.getItem(this.fogKey()) ?? "");
      if (!m || Number(m[1]) !== n) return;
      const bits = atob(m[2]!);
      for (let i = 0; i < n * n; i++) if ((bits.charCodeAt(i >> 3) >> (i & 7)) & 1) this.explored[i] = 1;
    } catch {
      // A garbled save: explore it all again.
    }
  }

  private saveFog(): void {
    if (!this.world || !this.fogN) return;
    const n = this.fogN;
    const bytes = new Uint8Array(Math.ceil((n * n) / 8));
    for (let i = 0; i < n * n; i++) if (this.explored[i]) bytes[i >> 3]! |= 1 << (i & 7);
    let bits = "";
    for (const b of bytes) bits += String.fromCharCode(b);
    try {
      localStorage.setItem(this.fogKey(), `${n}:${btoa(bits)}`);
    } catch {
      // Storage full or off: the fog just comes back next time.
    }
  }
}

/** A small tile of faint diagonal lines, for the fog. */
let hatchTile: HTMLCanvasElement | undefined;
function hatch(): HTMLCanvasElement {
  if (hatchTile) return hatchTile;
  hatchTile = document.createElement("canvas");
  hatchTile.width = hatchTile.height = 8;
  const g = hatchTile.getContext("2d")!;
  g.strokeStyle = "rgba(168,115,63,0.14)";
  g.lineWidth = 1;
  g.beginPath();
  g.moveTo(0, 8);
  g.lineTo(8, 0);
  g.stroke();
  return hatchTile;
}
